/**
 * Audio Processing panel — universal pre-engine signal conditioning that
 * lives in the Source tab. Binds DOM sliders to `globalAudioParams` so the
 * value applies to every scene/engine. Persists to localStorage so the
 * user's tuning survives reload.
 *
 * Phase 1 wiring (surface-only): see the notes per field — some sliders
 * are inert today and labeled as such in the UI.
 */

import {
  globalAudioParams,
  DEFAULT_AUDIO_PROCESSING,
  type AudioProcessingParams,
} from "../audio/analyser";

const STORAGE_KEY = "pfx_audioProcessing_v1";

type Key = keyof AudioProcessingParams;

const NUMERIC_KEYS: Key[] = [
  "fftSize",
  "dbFloor",
  "dbCeiling",
  "peakDecay",
  "midReactivity",
  "highReactivity",
  "gain",
  "noiseGate",
  "bassAttenuation",
  "spectrumCap",
  "spectrumCompress",
];

function load(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Partial<AudioProcessingParams>;
    const bag = globalAudioParams as unknown as Record<string, unknown>;
    for (const k of NUMERIC_KEYS) {
      const v = obj[k];
      if (typeof v === "number" && Number.isFinite(v)) {
        bag[k] = v;
      }
    }
    if (obj.spectrumFunction === "input" || obj.spectrumFunction === "processed") {
      globalAudioParams.spectrumFunction = obj.spectrumFunction;
    }
  } catch {
    /* corrupt blob — ignore */
  }
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(globalAudioParams));
  } catch {
    /* quota or private mode — ignore */
  }
}

function q<T extends HTMLElement>(sel: string): T | null {
  return document.querySelector<T>(sel);
}

function bindRange(
  id: string,
  key: Key,
  readoutId: string | null,
  fmt: (v: number) => string = (v) => v.toFixed(2),
): void {
  const el = q<HTMLInputElement>(`#${id}`);
  if (!el) return;
  const readout = readoutId ? q<HTMLElement>(`#${readoutId}`) : null;
  const bag = globalAudioParams as unknown as Record<string, number>;
  const initial = bag[key];
  el.value = String(initial);
  if (readout) readout.textContent = fmt(initial);
  el.addEventListener("input", () => {
    const v = Number(el.value);
    if (!Number.isFinite(v)) return;
    bag[key] = v;
    if (readout) readout.textContent = fmt(v);
    save();
  });
}

function bindSelect(id: string, key: Key, parse: (v: string) => unknown): void {
  const el = q<HTMLSelectElement>(`#${id}`);
  if (!el) return;
  const bag = globalAudioParams as unknown as Record<string, unknown>;
  el.value = String(bag[key]);
  el.addEventListener("change", () => {
    const v = parse(el.value);
    bag[key] = v;
    save();
  });
}

function bindResetButton(id: string): void {
  const el = q<HTMLButtonElement>(`#${id}`);
  if (!el) return;
  el.addEventListener("click", () => {
    Object.assign(globalAudioParams, DEFAULT_AUDIO_PROCESSING);
    save();
    initAudioProcessingPanel(); // re-render with defaults
  });
}

/**
 * Wire up the Audio Processing section in the Source tab. Safe to call
 * before/without the DOM elements present — each binder silently skips
 * missing elements, so this can also be called multiple times (e.g. on
 * reset) to refresh slider positions.
 */
export function initAudioProcessingPanel(): void {
  load();

  // FFT size — wired
  bindSelect("apFftSize", "fftSize", (v) => Number(v));
  // dB floor / ceiling — wired
  bindRange("apDbFloor", "dbFloor", "apDbFloorVal", (v) => `${v.toFixed(0)} dB`);
  bindRange("apDbCeiling", "dbCeiling", "apDbCeilingVal", (v) => `${v.toFixed(0)} dB`);
  // Peak decay — wired (analyser smoothingTimeConstant)
  bindRange("apPeakDecay", "peakDecay", "apPeakDecayVal");
  // Mid reactivity — wired
  bindRange("apMidReactivity", "midReactivity", "apMidReactivityVal");
  // High reactivity — wired
  bindRange("apHighReactivity", "highReactivity", "apHighReactivityVal");
  // Gain — wired
  bindRange("apGain", "gain", "apGainVal");

  // Noise gate — wired: zeros out bands below the gate threshold.
  bindRange("apNoiseGate", "noiseGate", "apNoiseGateVal");
  // Bass attenuation — wired: scales the published bass band down without
  // touching beat detection.
  bindRange("apBassAtten", "bassAttenuation", "apBassAttenVal");
  // Spectrum cap — wired: hard ceiling on each published band.
  bindRange("apSpectrumCap", "spectrumCap", "apSpectrumCapVal");
  // Spectrum compress — wired: log-shapes each band so quieter values lift.
  bindRange("apSpectrumCompress", "spectrumCompress", "apSpectrumCompressVal");
  // Spectrum function — wired: "input" publishes raw values, "processed"
  // (default) applies the full chain (gain, gate, atten, compress, cap).
  bindSelect("apSpectrumFunction", "spectrumFunction", (v) =>
    v === "input" ? "input" : "processed",
  );

  bindResetButton("apReset");
}
